import { useState, useRef, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import detectIt from 'detect-it';
import fetchVisibleItems from '@utils/viewportHelper';
import { getMonthString, getDay } from '@utils/dates';
import H4 from '@base/H4/H4';
import { breakpoints } from '@styles/index';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { getBatchScheduleClasses } from '@selectors/batches';
import BatchSchdeuleItem from './BatchScheduleItem';

let lastScrollTop = 0;

const ListWrapper = styled.div`
  grid-column: 1/17;
  background-color: var(--color-base-0);
  margin-top: -8px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    margin: 0;
  }
`;
const EmptyState = styled.div`
  grid-column: 7/18;
`;
const FixedDateHeader = styled.div`
  background-color: var(--color-base-0);
  height: 44px;
  width: 752px;
  z-index: 1;
  position: fixed;
  top: 224px;
  display: flex;
  align-items: center;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

const ItemsList = ({
  dispatchFetchScheduleData,
  setDatePointer,
  datePointer,
  goalUID,
  batchUID
}) => {
  const schedule = useSelectorToStore(getBatchScheduleClasses(batchUID));
  const [isFetchingData, setIsFetchingData] = useState(false);
  const VisibleBlocks = useRef();
  const isNewDate = [];
  schedule?.results?.map((item, index) => {
    if (index === 0) isNewDate[index] = true;
    else if (
      new Date(schedule.results[index - 1].liveAt).getDate() !==
      new Date(item.liveAt).getDate()
    )
      isNewDate[index] = true;
    else isNewDate[index] = false;
    return isNewDate;
  });

  const callNextUrl = useCallback(() => {
    setIsFetchingData(false);
  }, []);

  const loadMore = useCallback(
    (direction) => {
      setIsFetchingData(true);
      if (schedule) {
        if (direction === 'up')
          dispatchFetchScheduleData(schedule.previous, 'up', callNextUrl);
        else if (direction === 'down')
          dispatchFetchScheduleData(schedule.next, 'down', callNextUrl);
      }
    },
    [callNextUrl, dispatchFetchScheduleData, schedule]
  );

  const handleScroll = useCallback(() => {
    const listEl = document.getElementById('scheduleList');
    if (!listEl) return;
    const { clientHeight, scrollTop, scrollHeight } = listEl;
    const headerBlocks = document.getElementsByClassName('date-stamp');
    if (
      scrollTop > lastScrollTop &&
      scrollHeight / 2 <= clientHeight + scrollTop &&
      !isFetchingData
    )
      loadMore('down');
    else if (
      scrollTop < lastScrollTop &&
      scrollTop > 0 &&
      scrollTop < scrollHeight / 2 &&
      !isFetchingData
    )
      loadMore('up');

    VisibleBlocks.current = fetchVisibleItems(headerBlocks, listEl, 272);
    let currentBlock = VisibleBlocks.current[0];
    let blockTop = currentBlock?.getBoundingClientRect().y;
    if (blockTop < 18) {
      // eslint-disable-next-line prefer-destructuring
      currentBlock = VisibleBlocks.current[1];
      blockTop = currentBlock?.getBoundingClientRect().y;
    }
    if (blockTop < 280) {
      setDatePointer(Number(currentBlock.getAttribute('data-date')));
    }
    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  }, [isFetchingData, loadMore, setDatePointer]);

  useEffect(() => {
    const listEl = document.getElementById('scheduleList');
    if (listEl) {
      listEl.addEventListener(
        'scroll',
        handleScroll,
        detectIt.passiveEvents === true
          ? { capture: false, passive: true }
          : false
      );
      return () => listEl.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  const currentString = `${getMonthString(
    new Date(datePointer),
    'long'
  )} ${getDay(new Date(datePointer))}`;
  if (!schedule || schedule.length === 0) return <EmptyState />;
  return (
    <>
      <ListWrapper>
        <FixedDateHeader>
          <H4 color="var(--color-text-primary)">{currentString}</H4>
        </FixedDateHeader>
        {schedule?.results?.map((item, index) => (
          <BatchSchdeuleItem
            item={item}
            index={index}
            key={item?.uid}
            isNewDate={isNewDate}
            goalUID={goalUID}
          />
        ))}
      </ListWrapper>
    </>
  );
};

export default ItemsList;

ItemsList.propTypes = {
  dispatchFetchScheduleData: PropTypes.func.isRequired,
  setDatePointer: PropTypes.func.isRequired,
  datePointer: PropTypes.number.isRequired,
  goalUID: PropTypes.string,
  batchUID: PropTypes.string.isRequired
};

ItemsList.defaultProps = {
  goalUID: ''
};
